export default function ComparisonSection() {
  return (
    <section className="py-16 md:py-20 bg-background-100/50">
      <div className="max-w-[1280px] mx-auto px-4 md:px-6">
        <div className="flex flex-col lg:flex-row lg:items-start gap-6 mb-12 md:mb-16">
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight lg:w-1/2">
            ACBuy Spreadsheet vs
            <span className="block text-foreground-600 font-light italic text-2xl md:text-3xl lg:text-4xl mt-1">
              Traditional Shopping
            </span>
          </h2>
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed lg:w-1/2 lg:pt-2">
            Why thousands of smart shoppers switched from manual browsing to the{" "}
            <strong>acbuyspreadsheet</strong> system. Data beats guesswork every time.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          <div className="bg-background-50 border border-background-200/40 rounded-xl p-6 md:p-8">
            <div className="w-full h-36 rounded-lg overflow-hidden bg-background-100/50 mb-5">
              <img
                src="https://readdy.ai/api/search-image?query=Clean%20white%20desk%20with%20a%20single%20ultrawide%20monitor%20displaying%20organized%20spreadsheet%20data%2C%20warm%20lamplight%20glow%2C%20minimal%20cable%20management%2C%20small%20succulent%20plant%2C%20modern%20efficient%20workspace%2C%20soft%20evening%20ambiance%2C%20productivity%20concept&width=600&height=360&seq=compare-v2-acbuy&orientation=landscape"
                alt="ACBuy Spreadsheet interface"
                title="ACBuy Spreadsheet — organized product data"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <h3 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-3">
              ACBuy Spreadsheet
            </h3>
            <ul className="space-y-2 mb-4">
              <li className="flex items-start gap-2 text-sm text-foreground-600">
                <i className="ri-check-line text-secondary-600 mt-0.5 shrink-0"></i>
                <span>High price transparency across all suppliers</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-foreground-600">
                <i className="ri-check-line text-secondary-600 mt-0.5 shrink-0"></i>
                <span>Fast decision-making with side-by-side data</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-foreground-600">
                <i className="ri-check-line text-secondary-600 mt-0.5 shrink-0"></i>
                <span>Centralized product tracking in one place</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-foreground-600">
                <i className="ri-check-line text-secondary-600 mt-0.5 shrink-0"></i>
                <span>Significantly reduced shopping risk</span>
              </li>
            </ul>
            <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 text-xs font-semibold rounded-full">
              Recommended
            </span>
          </div>

          <div className="bg-background-50 border border-background-200/40 rounded-xl p-6 md:p-8">
            <div className="w-full h-36 rounded-lg overflow-hidden bg-background-100/50 mb-5">
              <img
                src="https://readdy.ai/api/search-image?query=Person%20looking%20confused%20at%20a%20messy%20desk%20with%20multiple%20paper%20receipts%20scattered%20around%20a%20laptop%2C%20dim%20shadowy%20lighting%2C%20cluttered%20workspace%2C%20overwhelmed%20shopping%20concept%2C%20dark%20wooden%20table%2C%20frustration%20mood%2C%20editorial%20lifestyle%20photography&width=600&height=360&seq=compare-v2-manual&orientation=landscape"
                alt="Traditional manual shopping"
                title="Traditional shopping — time-consuming process"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <h3 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-3">
              Manual Shopping
            </h3>
            <ul className="space-y-2 mb-4">
              <li className="flex items-start gap-2 text-sm text-foreground-500">
                <i className="ri-close-line text-foreground-400 mt-0.5 shrink-0"></i>
                <span>Low pricing visibility across stores</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-foreground-500">
                <i className="ri-close-line text-foreground-400 mt-0.5 shrink-0"></i>
                <span>Slow and fragmented comparison process</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-foreground-500">
                <i className="ri-close-line text-foreground-400 mt-0.5 shrink-0"></i>
                <span>No centralized product tracking</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-foreground-500">
                <i className="ri-close-line text-foreground-400 mt-0.5 shrink-0"></i>
                <span>Higher risk of overpaying</span>
              </li>
            </ul>
            <span className="inline-block px-3 py-1 bg-foreground-100 text-foreground-500 text-xs font-semibold rounded-full">
              Outdated
            </span>
          </div>

          <div className="bg-background-50 border border-primary-200/60 rounded-xl p-6 md:p-8">
            <div className="w-full h-36 rounded-lg overflow-hidden bg-background-100/50 mb-5">
              <img
                src="https://readdy.ai/api/search-image?query=Golden%20trophy%20on%20a%20pedestal%20with%20sparkling%20confetti%20on%20white%20marble%20surface%2C%20dramatic%20sunlight%20backlighting%2C%20achievement%20and%20excellence%20concept%2C%20clean%20minimal%20composition%2C%20luxurious%20editorial%20photography%2C%20bright%20white%20and%20gold%20accents&width=600&height=360&seq=compare-v2-winner&orientation=landscape"
                alt="ACBuy Spreadsheet is the clear winner"
                title="ACBuy Spreadsheet — clear winner for smart shopping"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <h3 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-3">
              Clear Winner
            </h3>
            <ul className="space-y-2 mb-4">
              <li className="flex items-start gap-2 text-sm text-foreground-600">
                <i className="ri-star-line text-accent-600 mt-0.5 shrink-0"></i>
                <span>4x faster product discovery</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-foreground-600">
                <i className="ri-star-line text-accent-600 mt-0.5 shrink-0"></i>
                <span>Save 30%+ on average purchases</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-foreground-600">
                <i className="ri-star-line text-accent-600 mt-0.5 shrink-0"></i>
                <span>15,000+ products tracked daily</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-foreground-600">
                <i className="ri-star-line text-accent-600 mt-0.5 shrink-0"></i>
                <span>Zero learning curve, instant value</span>
              </li>
            </ul>
            <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-xs font-semibold rounded-full">
              Winner
            </span>
          </div>
        </div>

        <div className="mt-12 bg-background-50 border border-background-200/40 rounded-xl overflow-hidden">
          <div className="grid grid-cols-3 text-sm">
            <div className="p-4 bg-background-100/50 font-semibold text-foreground-800">Feature</div>
            <div className="p-4 bg-primary-50/50 font-semibold text-primary-700 text-center">ACBuy Spreadsheet</div>
            <div className="p-4 bg-background-100/50 font-semibold text-foreground-500 text-center">Manual Shopping</div>
          </div>
          {[
            { feature: "Price Transparency", acbuy: "High", manual: "Low" },
            { feature: "Speed of Decision", acbuy: "Fast", manual: "Slow" },
            { feature: "Product Tracking", acbuy: "Yes", manual: "No" },
            { feature: "Shopping Risk", acbuy: "Lower", manual: "Higher" },
          ].map((row, idx) => (
            <div key={idx} className="grid grid-cols-3 text-sm border-t border-background-200/40">
              <div className="p-4 text-foreground-700">{row.feature}</div>
              <div className="p-4 text-center text-primary-700 font-medium bg-primary-50/20">
                <i className="ri-check-line text-secondary-600 mr-1"></i>{row.acbuy}
              </div>
              <div className="p-4 text-center text-foreground-500">
                <i className="ri-close-line text-foreground-400 mr-1"></i>{row.manual}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}